import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import http, { formatApiErrorDetail } from "@/api";
import { PILLARS } from "@/constants";
import { CheckCircle2, XCircle, ClipboardCheck, AlertTriangle } from "lucide-react";

const PILLAR_LABEL = Object.fromEntries(PILLARS.map((p) => [p.key, p.label]));
const fmtLv = (v) => (v == null ? "—" : v);

export default function KpiProposalsPanel() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () =>
    http.get("/admin/kpi-change-requests", { params: { status: "pending" } })
      .then((r) => setRows(r.data))
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const selectedIds = rows.filter((r) => sel[r.id]).map((r) => r.id);
  const allSelected = rows.length > 0 && selectedIds.length === rows.length;

  const decide = async (ids, action) => {
    if (action === "approve") {
      const ok = window.confirm(
        `${ids.length} solicitud(es) cambian el Objetivo fijo vigente. ` +
        "Al aceptar, el nuevo Objetivo aplicará como excepción administrada desde ese periodo. ¿Continuar?"
      );
      if (!ok) return;
    }
    let note = "";
    if (action === "reject") {
      note = window.prompt("Motivo del rechazo (lo verá el evaluador):") || "";
      if (!note) return;
    }
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await http.post("/admin/kpi-change-requests/decide", { ids, action, note });
      setMsg(action === "approve"
        ? `${data.approved} cambio(s) de Objetivo aceptado(s).`
        : `${data.rejected} cambio(s) rechazado(s) — el evaluador verá tu motivo.`);
      setSel({});
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-10" data-testid="kpi-proposals-panel">
      <div className="font-hand text-xl text-[var(--lala-blue)]">captura mensual</div>
      <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
        <ClipboardCheck size={20} className="text-[var(--lala-blue)]" /> Solicitudes de cambio de Objetivo
      </h2>
      <p className="text-sm text-[var(--ink-soft)] mb-3">
        Las evaluaciones normales se guardan directo y no requieren aprobación. Aquí solo aparecen excepciones donde un evaluador solicita cambiar el Objetivo fijo vigente.
      </p>

      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="kp-error">{err}</div>}
      {msg && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="kp-msg">{msg}</div>}

      {rows.length === 0 ? (
        <div className="sketch-border-soft bg-[var(--cream-soft)] p-6 text-sm text-[var(--ink-soft)]" data-testid="kp-empty">
          No hay solicitudes de cambio pendientes.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="kp-bulk-bar">
            <span className="text-sm">
              <b>{rows.length}</b> pendientes · <b className="text-[var(--lala-blue)]">{selectedIds.length}</b> seleccionadas
            </span>
            <button disabled={busy || selectedIds.length === 0}
              onClick={() => decide(selectedIds, "approve")}
              className="text-xs px-3 py-1.5 rounded-lg bg-green-700 text-white disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="kp-approve-selected">
              <CheckCircle2 size={13} /> Aceptar seleccionadas
            </button>
            <button disabled={busy || selectedIds.length === 0}
              onClick={() => decide(selectedIds, "reject")}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-700 text-red-700 disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="kp-reject-selected">
              <XCircle size={13} /> Rechazar seleccionadas
            </button>
          </div>
          <div className="sketch-border bg-[var(--cream-soft)] overflow-x-auto" data-testid="kp-list">
            <table className="w-full text-sm">
              <thead className="bg-[var(--cream-deep)]">
                <tr className="text-left">
                  <th className="px-3 py-2">
                    <input type="checkbox" checked={allSelected}
                      onChange={(e) => {
                        const next = {};
                        if (e.target.checked) rows.forEach((r) => { next[r.id] = true; });
                        setSel(next);
                      }}
                      className="accent-[var(--lala-blue)]" data-testid="kp-select-all" />
                  </th>
                  <th className="px-3 py-2">Proveedor</th>
                  <th className="px-3 py-2">Indicador</th>
                  <th className="px-3 py-2">Periodo</th>
                  <th className="px-3 py-2">Planta</th>
                  <th className="px-3 py-2">Objetivo vigente</th>
                  <th className="px-3 py-2">Objetivo solicitado</th>
                  <th className="px-3 py-2">Evaluador</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--line)]" data-testid={`kp-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={!!sel[r.id]}
                        onChange={(e) => setSel((p) => ({ ...p, [r.id]: e.target.checked }))}
                        className="accent-[var(--lala-blue)]" data-testid={`kp-check-${r.id}`} />
                    </td>
                    <td className="px-3 py-2">
                      <Link to={`/proveedores/${r.supplier_id}`} className="font-semibold text-[var(--lala-blue)] hover:underline">
                        {r.supplier_name}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">
                        {r.indicator_code} · {PILLAR_LABEL[r.pillar] || r.pillar}
                      </div>
                      <div className="text-xs">{r.indicator_label}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.period}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs" data-testid={`kp-plant-${r.id}`}>
                      {r.plant || <span className="text-[var(--ink-soft)]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[var(--ink-soft)]" data-testid={`kp-prev-${r.id}`}>
                      {fmtLv(r.previous?.objetivo_level)}
                      {r.previous?.period && <div className="text-[9px]">desde {r.previous.period}</div>}
                    </td>
                    <td className="px-3 py-2 font-bold text-[var(--lala-blue)]" data-testid={`kp-proposed-${r.id}`}>
                      <div className="inline-flex items-center gap-1">
                        <AlertTriangle size={12} className="text-amber-700" /> {fmtLv(r.objetivo_level)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[var(--ink-soft)]">
                      {r.proposed_by}
                      <div className="text-[10px]">{new Date(r.proposed_at).toLocaleString("es-MX")}</div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button disabled={busy} onClick={() => decide([r.id], "approve")}
                        className="text-green-700 hover:underline mr-3 inline-flex items-center gap-1"
                        data-testid={`kp-approve-${r.id}`}>
                        <CheckCircle2 size={14} /> Aceptar
                      </button>
                      <button disabled={busy} onClick={() => decide([r.id], "reject")}
                        className="text-red-700 hover:underline inline-flex items-center gap-1"
                        data-testid={`kp-reject-${r.id}`}>
                        <XCircle size={14} /> Rechazar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
